<template>
  <ul id="quote-progress">
    <li v-for="(step, index) in steps" :key="step.key" :class="progressClass(index + 1)">
      {{ step.label }}
    </li>
  </ul>
</template>

<script>
import AdditionalOrder from '@/mixins/additionalOrder.js'
import { getStatus } from '@/helpers/statusHelper'
import { mapState, mapGetters } from 'vuex'

export default {
  mixins: [AdditionalOrder],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    // Dynamic namespaces aren't supported by mapGetters
    ...mapState({
      state(state) {
        return state[this.quoteNamespace]
      },
      quote(state, getters) {
        return getters[this.quoteNamespace + '/get']
      },
      isStatusSalesOrder(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsSalesOrder']
      },
      isStatusQuote(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsQuote']
      },
      isSigningStatusEmpty(state, getters) {
        return getters[this.quoteNamespace + '/quoteSigningStatusIsEmpty']
      },
      isSigningStatusPending(state, getters) {
        return getters[this.quoteNamespace + '/quoteSigningStatusIsPending']
      },
      isSigningStatusCompleted(state, getters) {
        return getters[this.quoteNamespace + '/quoteSigningStatusIsCompleted']
      },
      isStatusConcept(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsConcept']
      },
      salesOrderSendToAutoline(state, getters) {
        return getters[this.quoteNamespace + '/salesOrderSendToAutoline']
      },
    }),
    ...mapGetters({
      dealerSettings: 'user/dealerSettings',
    }),
    status() {
      return this.quote?.Status
    },
    // A sales order from before the dealer switched approval on still follows the Autoline route.
    showsApprovalRoute() {
      return !!this.dealerSettings?.salesApprovalEnabled && !this.isStatusSalesOrder
    },
    approvalStatuses() {
      return ['CONCEPT', 'QUOTE', 'TO_APPROVE', 'APPROVED', 'PENDING', 'CONFIRMED']
    },
    steps() {
      if (!this.showsApprovalRoute) {
        return [
          { key: 'concept', label: this.i18n.t('/QuoteActions/ProgressBar/Concept') },
          { key: 'offerte', label: this.i18n.t('/QuoteActions/ProgressBar/Offerte') },
          { key: 'signpending', label: this.i18n.t('/QuoteActions/ProgressBar/SignPending') },
          { key: 'signcompleted', label: this.i18n.t('/QuoteActions/ProgressBar/SignCompleted') },
          { key: 'salesorder', label: this.i18n.t('/QuoteActions/ProgressBar/SalesOrder') },
        ]
      }
      return this.approvalStatuses.map((status) => ({
        key: status,
        label: status === this.rejectedAt ? getStatus('REJECTED') : this.approvalStepLabel(status),
      }))
    },
    // The step a rejected quote was rejected at: the sales manager's or the sales administration's.
    rejectedAt() {
      if (this.status !== 'REJECTED') {
        return null
      }
      const rejection = (this.quote?.Metadata?.History || [])
        .filter((item) => item.NewStatus === 'REJECTED')
        .sort((a, b) => String(b.UpdatedOn).localeCompare(String(a.UpdatedOn)))[0]
      return rejection?.Status === 'APPROVED' ? 'APPROVED' : 'TO_APPROVE'
    },
    currentStatusProgress() {
      if (this.showsApprovalRoute) {
        const current = this.rejectedAt || this.status
        return this.approvalStatuses.indexOf(current) + 1
      }

      let progress = 0
      if (this.isStatusConcept) progress = 1
      if (this.isStatusQuote) progress = 2
      if (this.isStatusSalesOrder) {
        if (this.salesOrderSendToAutoline) {
          progress = 5
        } else {
          if (this.isSigningStatusPending) progress = 3
          if (this.isSigningStatusCompleted) progress = 4
        }
      }

      return progress
    },
  },
  methods: {
    approvalStepLabel(status) {
      if (status === 'CONCEPT') return this.i18n.t('/QuoteActions/ProgressBar/Concept')
      if (status === 'QUOTE') return this.i18n.t('/QuoteActions/ProgressBar/Offerte')
      return getStatus(status)
    },
    progressClass(stepNumber) {
      let currentProgress = this.currentStatusProgress
      let progressClass = ''

      if (stepNumber < currentProgress) progressClass = 'done'
      else if (stepNumber === currentProgress)
        progressClass = this.rejectedAt ? 'rejected' : 'current'

      return progressClass
    },
  },
}
</script>

<style lang="scss" scoped>
#quote-progress {
  padding: 0;
  list-style-type: none;
  clear: both;
  line-height: 1em;
  margin: 0 -1px;
  text-align: center;
  display: flex;
  isolation: isolate;
}

#quote-progress li {
  flex: 1;
  float: left;
  padding: 10px 30px 10px 40px;
  background: #666;
  color: #fff;
  position: relative;
  border-top: 1px solid #666;
  border-bottom: 1px solid #666;
  margin: 0 1px;
}

#quote-progress li:before {
  content: '';
  border-left: 16px solid #fff;
  border-top: 16px solid transparent;
  border-bottom: 16px solid transparent;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
}
#quote-progress li:after {
  content: '';
  border-left: 16px solid #666;
  border-top: 16px solid transparent;
  border-bottom: 16px solid transparent;
  position: absolute;
  top: 0;
  left: 100%;
  height: 100%;
  z-index: 20;
}

#quote-progress li.done {
  background: var(--color-primary-dark);
}

#quote-progress li.done:after {
  border-left-color: var(--color-primary-dark);
}

#quote-progress li.current {
  background: var(--color-primary);
}

#quote-progress li.current:after {
  border-left-color: var(--color-primary);
}

#quote-progress li.rejected {
  background: var(--ds-color-danger-text);
}

#quote-progress li.rejected:after {
  border-left-color: var(--ds-color-danger-text);
}
</style>
